import React, { useState, useEffect } from "react";

import { FormControlLabel, Switch, Box, Stack, Divider } from "@mui/material";

import { useTranslation } from "react-i18next";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import RadarChartPreview from "./RadarChartPreview";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import SettingsLegend from "components/settingsConfig/uiComponents/SettingsLegend";
import { radarChartDefaultSettings, RadarChartSettingsStateType } from "./RadarChartUtils";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";


const defaultWidgetRequest = {
  name: 'radar-chart',
  endpoint: 'query/agg',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultRadarChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundKeywordFields = queryInfo.fields?.filter((field:QueryField) => {
    const numberOfOptions = parseInt(field?.options?.length + "");
    return ["keyword", "keywords"].includes(field.type) && numberOfOptions;
  }) || false;

  const foundKeywordFieldsFiltered = foundKeywordFields.filter((field: QueryField) => field.options && field.options.length >= 3);
  const foundKeywordField = foundKeywordFieldsFiltered[0];
  if (!foundKeywordField || !foundKeywordField.id || !foundKeywordField.label) {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  requestInitBody.aggs = {
      [foundKeywordField.label]: {
        "op": "keyword",
        "field": foundKeywordField.id,
          "aggs": {
            "percent": {
              "op": "percent",
              "of_total": true
            }
          }
      }
    };

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function RadarChartSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'radar-chart');
  const requestBody = originalRequest?.requestInit?.body || {};

  // Lookup and set the selected field from the widget request info
  const aggs = requestBody?.aggs || {};
  const aggsKeys = Object.keys(aggs || {});
  const firstKey = aggsKeys[0];
  const agg = aggs?.[firstKey];
  const aggField = agg?.field;
  const foundField = props.queryInfo?.fields?.find((field: QueryField) => field.id === aggField);

  const [selectedField, setSelectedField] = useState<QueryField | undefined>(foundField ?? undefined);
  const [includeNull, setIncludeNull] = useState<boolean>(requestBody?.aggs[firstKey].include_null ?? false);
  const [categoryCount, setCategoryCount] = useState<string>(props.widget?.data?.categoryCount ?? "5");

  // Get the categories that have at least 3 options for the chart to display as 3 is the minimum that can be displayed.
  const categoryOptions = props.queryInfo?.fields?.filter((field: QueryField) => field.options && field.options.length >= 3) ?? [];

  // Lookup the selected field and get the options for the category count. 10 is the maximum.
  // This is to limit the amount of sides in the chart based on the number of options available, without giving too many options.
  const categoryAmountOptions = Array.from({length: Math.min(selectedField?.options?.length || 0, 10)}, (option, i) => i + 1).slice(2,10);

  useEffect(() => {
    if(!categoryAmountOptions.includes(parseInt(categoryCount))) {
      setCategoryCount(categoryAmountOptions[0] + "");
    }
  }, [selectedField, categoryAmountOptions.length]);

  const updateWidget = () => {
    if (!selectedField) {
      return;
    }

    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const label:string = selectedField.label;
    const requestInitBody = { aggs: {} };

    const localAggs = {
      "op": "keyword",
      "field": selectedField.id,
      "aggs": {
        "percent": {
          "op": "percent",
          "of_total": true
        }
      }
    };

    requestInitBody.aggs = { [label]: localAggs };
    widgetRequest.requestInit = { body: requestInitBody };

    props.saveWidgetDataAndRequests({
      selectedField: selectedField,
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }


  const [settingsState, setSettingsState] = useState<RadarChartSettingsStateType>({...radarChartDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: RadarChartSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.radar-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 &&
          <>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <QueryFieldSelector
              requireAggSupported={true}
              fields={categoryOptions}
              allowedFieldTypes={["keyword", "keywords"]}
              disableClearable={true}
              selectedFieldId={selectedField?.id || ""}
              setSelectedFieldId={(newFieldId:string) => {
                const newSelectedField = categoryOptions?.find((field: QueryField) => field.id === newFieldId);
                setSelectedField(newSelectedField);
              }} />
            <Box sx={{ paddingTop: "10px", paddingLeft: "5px" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeNull}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setIncludeNull(event.target.checked);
                    }}/>
                }
                label={i18n.t("requestOptions.only-rows-with-value")} />
            </Box>
          </>
        }
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <RadarChartPreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                    keyid="widgetTitle"
                    label={i18n.t("title")}
                    showQueryOption={true}
                    widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle} />
                  </Stack>}
                  <SettingsToggleButton
                    keyid="widgetTitleAlignment"
                    label={i18n.t("widget-settings.alignment")}
                    optionsList={horizontalAlignmentOptions}
                    value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              {/* Graph Options */}
              <SettingsAccordion label={i18n.t("graph")} showBottomDivider={true}>
                <SettingsSlider
                  defaultValue={categoryAmountOptions.includes(5) ? 5 : categoryAmountOptions[0]}
                  // defaultValue={3}
                  keyid="categoryCount"
                  label={i18n.t("widget-types.radar-chart.settings.category-count")}
                  // max={categoryAmountOptions[0]}
                  max={categoryAmountOptions.length}
                  // min={1}
                  // min={categoryAmountOptions.includes(5) ? 3 : 1}
                  min={categoryAmountOptions.includes(5) ? 3 : 1}
                  sliderValue={settingsState.categoryCount}
                  // sliderValue={5}
                  showCurrentValue />
                <SettingsCheckbox
                  checked={settingsState.showLabel}
                  keyid="showLabel"
                  label={i18n.t("widget-settings.show-label")}
                />
                <SettingsCheckbox
                  checked={settingsState.showDots}
                  keyid="showDots"
                  label={i18n.t("widget-settings.show-dots")}
                />
                {settingsState.showDots &&
                  <SettingsSlider
                    defaultValue={3}
                    keyid={'dotSize'}
                    label={i18n.t("widget-types.sparkline.settings.dot-size")}
                    min={1}
                    max={10}
                    sliderValue={settingsState.dotSize ?? 3}
                  />
                }
              </SettingsAccordion>
              <SettingsLegend settingsState={settingsState} />
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>

    </TabbedDialog>
  );
}
